import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../utils/axios';
import { getFormattedError } from '../../utils/errorHandler';
import {
  Container,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Box,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { supportTheme } from "theme";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

interface IQueryOptionDB {
  _id: string;
  label: string;
  category: string;
  url: string;
  type: string;
  description?: string;
}

interface IQueryOption {
  id: string;
  label: string;
  url: string;
  description?: string;
}

interface IQueryList {
  [category: string]: Array<IQueryOption>;
}

const Redash: React.FC = () => {
  const [RedashQueryList, setRedashQueryList] = useState<IQueryList>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string>('');

  const adaptQueryList = (data: Array<IQueryOptionDB>, QueryList: IQueryList) => {
    data.forEach((item) => (
      QueryList[item.category] = []
    ))
    data.forEach((item) => (
      QueryList[item.category].push({ "id": item._id, "label": item.label, "url": item.url, "description": item.description })
    ))
    return QueryList
  }


  const fetchDB = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError('');
      const res = await axiosInstance.get("/queries/all-redash");
      const responseData = res.data;
      
      // Handle standardized response format { success: true, data: [...] }
      let RedashQueryListData = [];
      if ((responseData.success && responseData.data) || Array.isArray(responseData)) {
        RedashQueryListData = responseData.data || responseData;
      }
      
      if (RedashQueryListData.length > 0) {
        const RedashQueryListDataFormat: IQueryList = adaptQueryList(RedashQueryListData, {})
        setRedashQueryList(RedashQueryListDataFormat)
        const initialCategory = Object.keys(RedashQueryListDataFormat)[0]
        setCategory(initialCategory)
        setLabel(RedashQueryListDataFormat[initialCategory][0].label)
        setURL(RedashQueryListDataFormat[initialCategory][0].url)
      }
    } catch (err) {
      console.error('Error fetching Redash queries:', err);
      setFetchError(getFormattedError(err, 'fetch Redash queries'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDB();
  }, [fetchDB])

  const [cloudName, setCloudName] = useState('');
  const [commaSeparatedCloudName, setCommaSeparatedCloudName] = useState('');
  const [daysBackFrom, setdaysBackFrom] = useState('3');
  const [daysBackTo, setdaysBackTo] = useState('0');
  const [error, setError] = useState<string | null>(null); // State for validation error message
  const [category, setCategory] = useState('');
  const [label, setLabel] = useState('');
  const [url, setURL] = useState('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [accountId, setAccountId] = useState('');
  const [flagNum, setFlagNum] = useState('');
  const [flagValue, setFlagValue] = useState('');
  const [numberOfDays, setNumberOfDays] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [transformation, setTransformation] = useState('');
  const [prefix, setPrefix] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const fromDateInputRef = useRef<HTMLInputElement>(null);
  const toDateInputRef = useRef<HTMLInputElement>(null);

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const newCategory = event.target.value as string;
    setCategory(newCategory);
    setLabel(RedashQueryList[newCategory][0].label);
    setURL(RedashQueryList[newCategory][0].url);
  };

  const handleLabelChange = (event: SelectChangeEvent<string>) => {
    const newLabel = event.target.value as string;
    setLabel(newLabel);
    let queryItem = RedashQueryList[category].find((x) => x.label === newLabel);
    const newURL = (queryItem ? queryItem.url : "https://redash.cloudinary.com/queries/new")
    setURL(newURL)
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(''); // Clear previous errors

    const from = parseInt(daysBackFrom, 10);
    const to = parseInt(daysBackTo, 10);

    // Validation logic for date range
    if (from - to > 3 || from - to < 0) {
      setError('The difference between "Days Back From" and "Days Back To" must be between 0 and 3 days.');
      return; // Stop form submission if validation fails
    }

    const regexp = /\{(.*?)\}/g;
    let rawUrl = url;
    let matches = url.match(regexp);
    if (matches) {
      for (let match of matches) {
        let matchStringNoBracket = `` + match.replace(/[{]/g, '${')
        rawUrl = rawUrl.replaceAll(match, matchStringNoBracket);
      }
    }

    // Use template literal replacement instead of eval/Function constructor
    try {
      // Replace template variables with actual values
      let redashUrl = rawUrl
        .replace(/\$\{cloudName\}/g, cloudName)
        .replace(/\$\{daysBackFrom\}/g, daysBackFrom)
        .replace(/\$\{daysBackTo\}/g, daysBackTo)
        .replace(/\$\{date\}/g, fromDate)
        .replace(/\$\{fromDate\}/g, fromDate)
        .replace(/\$\{toDate\}/g, toDate)
        .replace(/\$\{accountId\}/g, accountId)
        .replace(/\$\{flagNum\}/g, flagNum)
        .replace(/\$\{flagValue\}/g, flagValue)
        .replace(/\$\{numberOfDays\}/g, numberOfDays)
        .replace(/\$\{customerId\}/g, customerId)
        .replace(/\$\{commaSeparatedCloudName\}/g, commaSeparatedCloudName)
        .replace(/\$\{countryCode\}/g, countryCode)
        .replace(/\$\{transformation\}/g, transformation)
        .replace(/\$\{prefix\}/g, prefix);

      window.open(redashUrl, '_blank');
    } catch (error) {
      setError('Error generating URL. Please check your input values.');
      console.error('Template error:', error);
    }
  };

  const navigate = useNavigate();
  const handleBackClick = () => {
    navigate('/dashboard/links-grid');
  };

  const handleNumericInput = (value: string) => {
    return `` + value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  };

  const formatCloudName = (value: string): string => {
    if (!value.trim()) return '';
    
    // Split by comma, format each segment with quotes
    return value
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map(name => {
        const cleaned = name.replace(/^['"]|['"]$/g, '');
        return `'${cleaned}'`;
      })
      .join(',');
  };

  const handleCloudNameChange = (value: string) => {
    if (!value.trim()) {
      setCommaSeparatedCloudName('');
      return;
    }
    
    // Format only completed segments (those with commas), leave current segment as-is
    if (value.includes(',')) {
      const parts = value.split(',');
      const lastPart = parts[parts.length - 1];
      const completedParts = parts.slice(0, -1);
      
      // Format completed segments with quotes
      const formattedCompleted = completedParts
        .map(name => name.trim())
        .filter(name => name.length > 0)
        .map(name => {
          const cleaned = name.replace(/^['"]|['"]$/g, '');
          return `'${cleaned}'`;
        })
        .join(',');
      
      // Combine formatted completed parts with the current segment being typed
      if (formattedCompleted) {
        setCommaSeparatedCloudName(`${formattedCompleted},${lastPart}`);
      } else {
        setCommaSeparatedCloudName(lastPart);
      }
    } else {
      // No comma yet, just store as-is (user is still typing)
      setCommaSeparatedCloudName(value);
    }
  };

  const handleCloudNameBlur = () => {
    // When user leaves the field, format the entire value including the last segment
    if (commaSeparatedCloudName.trim()) {
      setCommaSeparatedCloudName(formatCloudName(commaSeparatedCloudName));
    }
  };

  const handleFromDateInputClick = () => {
    if (fromDateInputRef.current) {
      // Try to use showPicker() if available (modern browsers)
      if ('showPicker' in HTMLInputElement.prototype) {
        fromDateInputRef.current.showPicker();
      } else {
        // Fallback: focus and click to trigger the date picker
        fromDateInputRef.current.focus();
        fromDateInputRef.current.click();
      }
    }
  };

  const handleToDateInputClick = () => {
    if (toDateInputRef.current) {
      // Try to use showPicker() if available (modern browsers)
      if ('showPicker' in HTMLInputElement.prototype) {
        toDateInputRef.current.showPicker();
      } else {
        // Fallback: focus and click to trigger the date picker
        toDateInputRef.current.focus();
        toDateInputRef.current.click();
      }
    }
  };

  const appTheme = supportTheme();
  return (
    <ThemeProvider theme={appTheme}>
      <Box mt={2}>
        <Button variant="contained" onClick={handleBackClick}>
          Back
        </Button>
      </Box>
      <Container maxWidth="md">
        <Box my={4}>
          <Typography variant="overline" component="h1" gutterBottom sx={{ fontSize: "1.75rem" }}>
            Generate Redash Query
          </Typography>
          <Alert 
            severity='info' 
            variant='filled'
            sx={{ 
              backgroundColor: '#3448c5',
              color: '#ffffff'
            }}
          >
            <AlertTitle sx={{ fontWeight: "bold", color: '#ffffff' }}>Performance Notice</AlertTitle>
            Queries are resource-intensive operations. To ensure optimal performance and faster results, please restrict the date range and limit report details when configuring your query parameters.
          </Alert>
          <br />
          {fetchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fetchError}
            </Alert>
          )}
          {loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading Redash queries...
            </Alert>
          )}
          {(RedashQueryList && Object.keys(RedashQueryList).length > 0) &&
            <form onSubmit={handleSubmit}>
              <Box mb={2}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: "#1d2438" }}>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={handleCategoryChange}
                  >
                    {Object.keys(RedashQueryList).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box mb={2}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: "#1d2438" }}>Query/Dashboard</InputLabel>
                  <Select
                    value={label}
                    label="Query/Dashboard"
                    onChange={handleLabelChange}
                    defaultValue=""
                  >
                    {(Object.keys(RedashQueryList).length > 0) && RedashQueryList[category].map((opt) => (
                      <MenuItem key={opt.id} value={opt.label}>
                        {opt.label}
                        {opt.description &&
                          <Tooltip sx={{ fill: '#3448c5', width: '0.5em', height: '0.5em', marginLeft: '5px' }} title={opt.description}>
                            <InfoIcon />
                          </Tooltip>
                        }
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              


              {/* Conditionally render the following fields if "Bandwidth Dashboard" is selected */}
              {label === "Bandwidth dashboard" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Cloud Name"
                      value={cloudName}
                      onChange={(e) => setCloudName(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Days Back From: "
                      value={daysBackFrom}
                      onChange={(e) => setdaysBackFrom(handleNumericInput(e.target.value))}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Days Back To:"
                      value={daysBackTo}
                      onChange={(e) => setdaysBackTo(handleNumericInput(e.target.value))}
                      required
                    />
                  </Box>

                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "List of IPs that have highest bandwidth when Referer is N/A" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer ID"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate }
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "List of users of an account" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Account ID"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "List of active clouds that have a specified flag enabled" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Flag Num"
                      value={flagNum}
                      onChange={(e) => setFlagNum(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Flag Value"
                      value={flagValue}
                      onChange={(e) => setFlagValue(e.target.value)}
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Add-on usage for all clouds for the last X days" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Account Id"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Number Of Days"
                      value={numberOfDays}
                      onChange={(e) => setNumberOfDays(e.target.value)}
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Usage of Named Transformations" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Top Transformations and their public ids" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Top 100 Impressions for unique public_ids transformations" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Average File Size of all original videos and images" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Please enter commma between cloud names. Example: abc,def"
                      value={commaSeparatedCloudName}
                      onChange={(e) => handleCloudNameChange(e.target.value)}
                      onBlur={handleCloudNameBlur}
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}

                </>
              )}

              {label === "Top Request IPs and their counts from a Country" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Country. Please enter 2 Character Country Code e.g. US"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      required
                    />
                  </Box>
                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              {label === "Daily Report - Total Storage, Bandwidth" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Account Id"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Report Time"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Report Time"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      required
                    />
                  </Box>


                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              {label === "Number of 404 errors in a specific day" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>

                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              {label === "File Size for all assets with x transformation last accessed" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Transformation"
                      value={transformation}
                      onChange={(e) => setTransformation(e.target.value)}
                      required
                    />
                  </Box>

                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              {label === "List all DAM users actions" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Account Id"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Report Time"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Report Time"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      required
                    />
                  </Box>

                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              {label === "Count distinct requests for prefix" && (
                <>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Customer Id"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputRef={fromDateInputRef}
                      slotProps={{ input: { onClick: handleFromDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputRef={toDateInputRef}
                      slotProps={{ input: { onClick: handleToDateInputClick }, inputLabel: { shrink: true } }}
                      fullWidth
                      variant="outlined"
                      required
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="Prefix"
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      required
                    />
                  </Box>

                  {/* Display validation error */}
                  {error && (
                    <Typography variant="body2" sx={{ color: "error" }} >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </form>
          }
          <br />
          Queries here are from the confluence page: <a style={{ color: "blue" }} href="https://cloudinary.atlassian.net/wiki/spaces/CS/pages/1029013535/Useful+SQL+Queries+for+Snowflake+Redash" target="_blank" rel="noopener noreferrer">Useful SQL Queries for Snowflake Redash</a>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Redash;
